import type {
  Branch,
  ChangePasswordWithTokenData,
  ChangePwWithTokenRequest,
  CheckPasswordResetTokenData,
  CheckPasswordResetTokenParams,
  CommonResponsePasswordRequiredResponse,
  CreatePasswordResetTokenData,
  CreateTokenRequest,
  CreverseStudentInfoRequest,
  CreverseStudentLoginRequest,
  CreverseTeacherLoginRequest,
  CreverseTeacherStaffInfoRequest,
  ForceChangePasswordData,
  GenerateStudentImpersonationAuthTokenByData,
  GetPasswordRequiredParams,
  GetSwitchCandidatesData,
  GetTeacherServiceTypeAccessData,
  GrantTeacherServiceAccessData,
  LoginRequest,
  LoginResponse,
  LoginWithNaverWorksRequest,
  LoginWithSamlAiepRequest,
  LoginWithWeizmannLegacyRequest,
  LoginWithWeizmannRequest,
  Logout1Data,
  LogoutData,
  LogoutRequest as LogoutRequestWire,
  PasswordChangeRequest as ForcePasswordChangeRequest,
  PasswordRequest,
  PasswordResetRequest,
  ResetPasswordData,
  StudentImpersonationAuthTokenGenerationRequest,
  StudentImpersonationLoginRequest,
  TeacherServiceAccessRequest,
  TeacherSwitchRequest as TeacherSwitchRequestWire,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestConfig, type RequestMeta } from '~/http/client'

import type { ServiceType } from '../Student'
import {
  adaptLoginInfo,
  adaptLoginSession,
  adaptStudentImpersonationAuthToken,
  adaptTeacherSwitchCandidates,
} from './adapters'
import type {
  LoginInfo,
  LoginSession,
  StudentImpersonationAuthToken,
  TeacherSwitchCandidates,
} from './schemas'

export type LoginInfoParams = GetPasswordRequiredParams
export type PasswordChangeRequest = PasswordRequest
// 로그인 화면의 입력 모양. serviceType 은 앱이 로그인 요청을 만들 때 붙인다.
export type LoginCredentials = Pick<LoginRequest, 'id' | 'password' | 'userType'>

export type { LoginRequest }
// 생성 타입은 deviceId 를 optional 로만 그리지만 서버 선언은 `deviceId: String? = null` 이라 null 도 받는다.
// 2026-10-02 백엔드 origin/develop 의 LogoutRequest.kt 로 확인.
export type LogoutRequest = Omit<LogoutRequestWire, 'deviceId'> & {
  deviceId?: LogoutRequestWire['deviceId'] | null
}
export type NaverWorksLoginRequest = LoginWithNaverWorksRequest
export type AiepLoginRequest = LoginWithSamlAiepRequest
export type WeizmannLoginRequest = LoginWithWeizmannLegacyRequest
export type WeizmannOauth2LoginRequest = LoginWithWeizmannRequest
export type StudentImpersonationAuthTokenRequest = StudentImpersonationAuthTokenGenerationRequest

// 크레버스 SSO 2페이즈 로그인 요청은 @mathflat/creverse-auth 가 보내므로 요청 함수 없이 계약 타입만 발행한다.
export type {
  CreverseStudentInfoRequest,
  CreverseStudentLoginRequest,
  CreverseTeacherLoginRequest,
  CreverseTeacherStaffInfoRequest,
}
export type CreverseBranch = Branch

/**
 * 크레버스 SSO 1페이즈 — 토큰으로 주체와 지점 목록을 받는다. 생성 스펙에 아직 없어 손으로 적는다.
 */
export type CreverseUserByTokenRequest = {
  ssoToken: string
}

export type CreverseUserByToken = {
  type: 'STUDENT' | 'PARENT'
  branches: CreverseBranch[]
  /** 해석하지 말고 보관했다가 2페이즈 로그인 body 에 그대로 담는다. 발급 후 5분 뒤 만료 */
  signedInfo: string
}

/**
 * 크레버스 학부모 2페이즈 로그인 요청. 1페이즈가 돌려준 `signedInfo` 를 해석하지 않고 그대로 싣는다.
 * 생성 스펙에 아직 없어 손으로 적는다.
 */
export type CreverseParentLoginRequest = {
  signedParentInfo: string
  brchId: CreverseBranch['brchId']
}

/**
 * 같은 사람의 다른 소속 선생님 계정으로 전환하는 요청.
 *
 * 학원이 아니라 `teacherId` 로 보낸다 — 학원마다 teacher 행이 따로라 한 사람이 같은 학원에
 * 두 행을 갖더라도 어느 쪽인지 모호해지지 않는다.
 *
 * 리프레시 토큰을 바디에 담는 이유는 BE 가 이전 리프레시의 jti 를 알아야 무효화할 수 있기
 * 때문이다 (`/v2/logout` 과 같은 이유). 액세스 토큰에는 그 값이 없다.
 */
export type TeacherSwitchRequest = TeacherSwitchRequestWire

export const getLoginInfo = async (
  params: LoginInfoParams,
  meta?: RequestMeta,
): Promise<LoginInfo> => {
  const body = await httpClient().get<CommonResponsePasswordRequiredResponse>('/login/info', {
    params,
    meta,
  })
  return adaptLoginInfo(body.data)
}

export const changePassword = async (
  body: PasswordChangeRequest,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<unknown>('/v2/password', body, { meta })
}

// 로그인한 선생님이 같은 학원 선생님·학생·학부모의 비밀번호를 정해 준 값으로 바꾼다. relationId 는 바꿀 사용자의 id 다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 authentication/controller/AuthenticationController.kt 로 확인.
export type PasswordResetBody = PasswordResetRequest

export const resetPassword = async (body: PasswordResetBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().patch<ResetPasswordData>('/password', body, { meta })
}

/***************** 비밀번호 찾기 *****************/
// 로그인 전에 쓰는 세 단계다. 아이디·이메일로 재설정 메일을 받고, 메일 링크의 토큰을 확인한 뒤 새 비밀번호로 바꾼다.
// 토큰이 없거나 만료되면 서버가 오류로 응답한다. 모두 응답 본문을 쓰지 않는다.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 authentication/controller/AuthenticationController.kt 로 확인.
export type PasswordResetTokenBody = CreateTokenRequest

export const requestPasswordResetToken = async (
  body: PasswordResetTokenBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CreatePasswordResetTokenData>('/password/reset-token', body, { meta })
}

export type PasswordResetTokenParams = CheckPasswordResetTokenParams

export const checkPasswordResetToken = async (
  params: PasswordResetTokenParams,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().get<CheckPasswordResetTokenData>('/password/check-reset-token', {
    params,
    meta,
  })
}

export type PasswordChangeWithTokenBody = ChangePwWithTokenRequest

export const changePasswordWithResetToken = async (
  body: PasswordChangeWithTokenBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<ChangePasswordWithTokenData>('/password/with-reset-token', body, {
    meta,
  })
}

/***************** 로그인·로그아웃 *****************/
// 로그인 계열 응답은 CommonResponse 봉투 없이 LoginResponse 가 그대로 온다.

/**
 * 아이디·비밀번호 로그인. 토큰 없이 보낸다 — 로그인 거부도 401 로 오므로, 토큰을 붙이면 재발급 인터셉터가
 * 세션 만료로 오해해 남아 있던 토큰까지 지운다. 화면이 거부 사유를 직접 안내하면 `preventErrorToast` 를 넘긴다.
 */
export const login = async (
  body: LoginRequest,
  { preventErrorToast }: Pick<RequestConfig, 'preventErrorToast'> = {},
): Promise<LoginSession> =>
  adaptLoginSession(
    await httpClient().post<LoginResponse>('/v2/login', body, {
      withoutAuth: true,
      preventErrorToast,
    }),
  )

/** 네이버 웍스(웨일스페이스) SSO 콜백의 code 로 로그인한다 */
export const loginWithNaverWorks = async (body: NaverWorksLoginRequest): Promise<LoginSession> =>
  adaptLoginSession(await httpClient().post<LoginResponse>('/v2/login/oauth2/naver-works', body))

/** AIEP(광주교육청 통합 로그인) SAML 콜백의 code 로 로그인한다 */
export const loginWithAiep = async (body: AiepLoginRequest): Promise<LoginSession> =>
  adaptLoginSession(await httpClient().post<LoginResponse>('/v2/login/saml2/aiep', body))

export const loginWithWeizmannLegacy = async (body: WeizmannLoginRequest): Promise<LoginSession> =>
  adaptLoginSession(
    await httpClient().post<LoginResponse>('/v2/login/oauth2/weizmann-legacy', body),
  )

export const loginWithWeizmann = async (body: WeizmannOauth2LoginRequest): Promise<LoginSession> =>
  adaptLoginSession(await httpClient().post<LoginResponse>('/v2/login/oauth2/weizmann', body))

type ImpersonationLoginToken = StudentImpersonationLoginRequest['studentImpersonationAuthToken']

/** 선생님 앱이 발급한 대리 로그인 토큰으로 학생 앱에 로그인한다. 아이디 로그인과 같은 이유로 토큰 없이 보낸다 */
export const loginWithStudentImpersonationAuthToken = async (
  studentImpersonationAuthToken: ImpersonationLoginToken,
): Promise<LoginSession> =>
  adaptLoginSession(
    await httpClient().post<LoginResponse>(
      '/v2/login/with-student-impersonation-auth-token',
      { studentImpersonationAuthToken } satisfies StudentImpersonationLoginRequest,
      { withoutAuth: true },
    ),
  )

export const logout = async (body: LogoutRequest): Promise<void> => {
  await httpClient().post<LogoutData>('/v2/logout', body)
}

/** 구 인증 토큰을 무효화한다. 구 토큰은 Bearer 가 아니라 `x-auth-token` 헤더로 싣는다 */
export const logoutLegacy = async (legacyToken: string, meta?: RequestMeta): Promise<void> => {
  await httpClient().post<Logout1Data>(
    '/logout',
    {},
    { headers: { 'x-auth-token': legacyToken }, meta },
  )
}

/***************** 소속 전환·대리 로그인 *****************/
export const getTeacherSwitchCandidates = async (
  serviceType: TeacherSwitchRequest['serviceType'],
): Promise<TeacherSwitchCandidates> =>
  adaptTeacherSwitchCandidates(
    await httpClient().get<GetSwitchCandidatesData>('/v2/teachers/me/switch-candidates', {
      params: { serviceType },
    }),
  )

/** 전환 응답은 로그인 응답과 같은 모양이다 */
export const switchTeacher = async (body: TeacherSwitchRequest): Promise<LoginSession> =>
  adaptLoginSession(await httpClient().post<LoginResponse>('/v2/teachers/me/switch', body))

/** 선생님 앱에서 학생 앱을 대리 로그인 상태로 열 일회용 토큰을 받는다 */
export const createStudentImpersonationAuthToken = async (
  body: StudentImpersonationAuthTokenRequest,
): Promise<StudentImpersonationAuthToken> =>
  adaptStudentImpersonationAuthToken(
    await httpClient().post<GenerateStudentImpersonationAuthTokenByData>(
      '/v2/student-impersonation-auth-token',
      body,
    ),
  )

/***************** 서비스 이용 *****************/
export type ServiceTypeAccessGrantBody = TeacherServiceAccessRequest

/** 선생님이 다른 서비스(영어·과학) 이용에 동의한다 */
export const grantServiceTypeAccess = async (body: ServiceTypeAccessGrantBody): Promise<void> => {
  await httpClient().post<GrantTeacherServiceAccessData>('/teachers/service-types/access', body)
}

/**
 * 다른 서비스로 넘어가기 전에 접근할 수 있는지 검사한다. 통과일 때만 200 이고 아니면 오류로 응답하므로
 * 본문의 accessible 은 늘 true 다. 그래서 본문은 읽지 않고 성공 여부로만 판단한다.
 */
export const checkServiceTypeAccess = async (serviceType: ServiceType): Promise<void> => {
  await httpClient().get<GetTeacherServiceTypeAccessData>(
    `/teachers/service-types/${serviceType}/access`,
  )
}

/***************** 강제 비밀번호 변경 *****************/
export type ForcePasswordChangeBody = ForcePasswordChangeRequest

/** 요청 위변조를 막는 서명. 호출부가 요청 시각과 HMAC 을 계산해 넘긴다 */
export type ForcePasswordChangeSignature = {
  requestDatetime: string
  authSignature: string
}

/** 비밀번호 변경이 강제된 계정이 첫 로그인 뒤 새 비밀번호를 정한다 */
export const forceChangePassword = async (
  body: ForcePasswordChangeBody,
  { requestDatetime, authSignature }: ForcePasswordChangeSignature,
): Promise<void> => {
  await httpClient().patch<ForceChangePasswordData>('/v2/password/force-change', body, {
    headers: { 'X-Request-Datetime': requestDatetime, 'X-Auth-Signature': authSignature },
  })
}
